import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, ClipboardCheck, AlertCircle, CheckCircle } from 'lucide-react';
import api from '@/utils/api';

export default function AssessmentInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    validateInvite();
  }, [token]);

  const validateInvite = async () => {
    try {
      const response = await api.post('/invites/validate', null, {
        params: { token }
      });
      setCandidate(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Link de convite inválido');
      } else if (err.response?.status === 400) {
        setError(err.response.data.detail || 'Link expirado ou avaliação já concluída');
      } else {
        setError('Erro ao validar convite');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStart = () => {
    navigate(`/avaliacao/${token}/iniciar`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 via-gray-100 to-slate-200">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Validando convite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-100 via-gray-100 to-slate-200">
        <Card className="w-full max-w-md shadow-premium border-0 glass">
          <CardHeader>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <CardTitle className="text-xl">Link Inválido</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">Entre em contato com o RH da DRSUPORTI para receber um novo link de avaliação.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-100 via-gray-100 to-slate-200">
      <div className="w-full max-w-2xl animate-slide-in">
        <Card className="shadow-premium border-0 glass" data-testid="welcome-card">
          <CardHeader className="text-center pb-4">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center mx-auto mb-6 shadow-lg">
              <ClipboardCheck className="w-10 h-10 text-white" />
            </div>
            <CardTitle className="text-3xl mb-3" style={{fontFamily: 'Manrope'}}>
              Oi{candidate?.full_name ? `, ${candidate.full_name.split(' ')[0]}` : ''}! Eu sou a Cíntia 👋
            </CardTitle>
            <p className="text-lg text-gray-700 leading-relaxed">
              Bora descobrir como você pensa, reage e trabalha em equipe? É rápido — só 7 minutinhos!
            </p>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="font-semibold text-blue-900 mb-3 flex items-center">
                <CheckCircle className="w-5 h-5 mr-2" />
                Como funciona:
              </h3>
              <ul className="space-y-2 text-blue-800">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>São 20 perguntas sobre como você age no dia a dia</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Não existe resposta certa ou errada — seja sincero(a)!</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Leva cerca de 7 minutos para completar</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>No final, você vai ver seu perfil comportamental (DISC)</span>
                </li>
              </ul>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-amber-900 text-sm">
                <strong>💡 Dica da Cíntia:</strong> Responda o que você faria <strong>na prática</strong>, não o que você acha que esperam de você. Isso garante um resultado mais preciso!
              </p>
            </div>

            <div className="pt-4">
              <Button
                onClick={handleStart}
                className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-semibold h-12 text-lg"
                data-testid="start-assessment-button"
              >
                Iniciar Avaliação
              </Button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              Ao iniciar, você concorda em compartilhar suas respostas com a equipe de RH da DRSUPORTI
            </p>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-gray-600 mt-6">
          © 2024 DRSUPORTI - Plataforma de Avaliação DISC
        </p>
      </div>
    </div>
  );
}
